import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import DataTable from '../../components/DataTable';
import BulkActions from '../../components/BulkActions';
import ColumnConfig from '../../components/ColumnConfig';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Users,
  Eye,
  CheckCircle,
  Plus,
  Pencil,
  UserPlus,
  GraduationCap,
  PauseCircle,
  XCircle,
  UserX,
  Loader2,
} from 'lucide-react';
import { DialogDescription } from '../../components/ui/dialog';

const Players = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [players, setPlayers] = useState([]);
  const [parents, setParents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');  // Default to Active players
  const [visibleColumns, setVisibleColumns] = useState([]);
  
  // Dialogs
  const [addEditDialog, setAddEditDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [bulkUpdateDialog, setBulkUpdateDialog] = useState(false);
  
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Bulk selection state
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
  const [bulkUpdateForm, setBulkUpdateForm] = useState({
    skill_level: '',
    gender: '',
    // Uniform
    shirt_name: '',
    shirt_number: '',
    // Measurements
    chest_size: '',
    shirt_length: '',
    sleeves: '',
    waist_size: '',
    trouser_length: ''
  });
  
  // Form data for add/edit player
  const [playerForm, setPlayerForm] = useState({
    full_name: '',
    date_of_birth: '',
    gender: '',
    skill_level: 'beginner',
    medical_notes: '',
    parent_id: '',
    // Uniform personalization
    shirt_name: '',
    shirt_number: '',
    // Measurements
    chest_size: '',
    shirt_length: '',
    sleeves: '',
    waist_size: '',
    trouser_length: '',
    // Cricclubs info
    cricclubs_id: '',
    cricclubs_email: '',
    // Contact info
    player_mobile: '',
    player_email: ''
  });
  
  // Form data for assignment
  const [assignForm, setAssignForm] = useState({
    program_id: '',
    selected_classes: [],  // Array of class IDs for multi-select
    payment_frequency: 'monthly',
    monthly_fee: '',
    total_fee: ''
  });

  // Helper function to determine player status based on enrollments
  const getPlayerStatus = (player) => {
    const enrollments = player.enrollments || [];
    if (enrollments.length === 0) return 'not_enrolled';
    
    // Check for any active/current enrollment
    const hasActive = enrollments.some(e => 
      e.status === 'current' || e.status === 'active' || e.status === 'approved'
    );
    if (hasActive) return 'active';
    
    // Check for any paused enrollment
    const hasPaused = enrollments.some(e => e.status === 'paused');
    if (hasPaused) return 'paused';
    
    // Check for cancelled
    const hasCancelled = enrollments.some(e => 
      e.status === 'canceled' || e.status === 'cancelled'
    );
    if (hasCancelled) return 'cancelled';
    
    return 'not_enrolled';
  };

  // Filter players by status tab
  const getFilteredPlayers = () => {
    if (activeTab === 'all') return players;
    return players.filter(player => getPlayerStatus(player) === activeTab);
  };

  // Count players by status
  const getStatusCounts = () => {
    const counts = { all: players.length, active: 0, paused: 0, cancelled: 0, not_enrolled: 0 };
    players.forEach(player => {
      const status = getPlayerStatus(player);
      counts[status]++;
    });
    return counts;
  };

  const fetchData = async () => {
    try {
      const [playersRes, parentsRes, programsRes, classesRes] = await Promise.all([
        api.get('/players'),
        api.get('/parents'),
        api.get('/programs'),
        api.get('/classes')
      ]);
      setPlayers(playersRes.data);
      setParents(parentsRes.data);
      setPrograms(programsRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle highlight parameter from URL (e.g., when navigating from parent profile)
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && players.length > 0) {
      const playerToHighlight = players.find(p => p.id === highlightId);
      if (playerToHighlight) {
        // Navigate to the player's detail page
        navigate(`/admin/players/${highlightId}`);
        // Clear the highlight param
        searchParams.delete('highlight');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [players, searchParams, navigate]);

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Bulk Update Functions
  const openBulkUpdateDialog = () => {
    setBulkUpdateForm({
      skill_level: '',
      gender: '',
      shirt_name: '',
      shirt_number: '',
      chest_size: '',
      shirt_length: '',
      sleeves: '',
      waist_size: '',
      trouser_length: ''
    });
    setBulkUpdateDialog(true);
  };

  const executeBulkUpdate = async () => {
    if (selectedPlayerIds.length === 0) {
      toast.error('No players selected');
      return;
    }

    // Build update data - only include non-empty fields
    const updateData = {};
    Object.entries(bulkUpdateForm).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        updateData[key] = value;
      }
    });

    if (Object.keys(updateData).length === 0) {
      toast.error('Please fill in at least one field to update');
      return;
    }

    setBulkUpdateLoading(true);
    let successCount = 0;
    let failCount = 0;

    try {
      for (const playerId of selectedPlayerIds) {
        try {
          await api.put(`/players/${playerId}`, updateData);
          successCount++;
        } catch (err) {
          failCount++;
          console.error(`Failed to update player ${playerId}:`, err);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully updated ${successCount} player(s)`);
      }
      if (failCount > 0) {
        toast.error(`Failed to update ${failCount} player(s)`);
      }

      setBulkUpdateDialog(false);
      setSelectedPlayerIds([]);
      fetchData();
    } catch (error) {
      toast.error('Bulk update failed');
    } finally {
      setBulkUpdateLoading(false);
    }
  };

  const clearSelection = () => {
    setSelectedPlayerIds([]);
  };

  const openAddDialog = () => {
    setIsEditing(false);
    setPlayerForm({
      full_name: '',
      date_of_birth: '',
      gender: '',
      skill_level: 'beginner',
      medical_notes: '',
      parent_id: '',
      // Measurements
      chest_size: '',
      shirt_length: '',
      sleeves: '',
      waist_size: '',
      trouser_length: '',
      // Cricclubs info
      cricclubs_id: '',
      cricclubs_email: '',
      // Contact info
      player_mobile: '',
      player_email: ''
    });
    setAddEditDialog(true);
  };

  const openEditDialog = (player) => {
    setIsEditing(true);
    setSelectedPlayer(player);
    setPlayerForm({
      full_name: player.full_name || player.name || '',
      date_of_birth: player.date_of_birth || '',
      gender: player.gender || '',
      skill_level: player.skill_level || 'beginner',
      medical_notes: player.medical_notes || '',
      parent_id: player.parent_id || '',
      // Uniform personalization
      shirt_name: player.shirt_name || '',
      shirt_number: player.shirt_number || '',
      // Measurements
      chest_size: player.chest_size || '',
      shirt_length: player.shirt_length || '',
      sleeves: player.sleeves || '',
      waist_size: player.waist_size || '',
      trouser_length: player.trouser_length || '',
      // Cricclubs info
      cricclubs_id: player.cricclubs_id || '',
      cricclubs_email: player.cricclubs_email || '',
      // Contact info
      player_mobile: player.player_mobile || '',
      player_email: player.player_email || ''
    });
    setAddEditDialog(true);
  };

  const openAssignDialog = (player) => {
    setSelectedPlayer(player);
    setAssignForm({
      program_id: '',
      selected_classes: [],
      payment_frequency: 'monthly',
      monthly_fee: '',
      total_fee: ''
    });
    setAssignDialog(true);
  };

  // Calculate fee based on program pricing and payment plan
  const calculateFee = (programId, paymentFrequency) => {
    const program = programs.find(p => p.id === programId);
    if (!program) return { monthly: '', total: '' };

    // Get the price based on payment frequency from program pricing
    let pricePerMonth;
    let totalFee;
    
    switch (paymentFrequency) {
      case 'quarterly':
        pricePerMonth = program.quarterly_price || program.price;
        totalFee = pricePerMonth * 3;
        break;
      case 'biannual':
        pricePerMonth = program.biannual_price || program.price;
        totalFee = pricePerMonth * 6;
        break;
      default: // monthly
        pricePerMonth = program.price || 0;
        totalFee = pricePerMonth;
    }

    return {
      monthly: pricePerMonth,
      total: totalFee.toFixed(2)
    };
  };

  // Update fee when program is selected
  const handleProgramChange = (programId) => {
    const fees = calculateFee(programId, assignForm.payment_frequency);
    setAssignForm({ 
      ...assignForm, 
      program_id: programId, 
      selected_classes: [],
      monthly_fee: fees.monthly,
      total_fee: fees.total
    });
  };

  // Toggle class selection (multi-select)
  const handleClassToggle = (classId) => {
    const currentSelected = assignForm.selected_classes || [];
    let newSelected;
    
    if (currentSelected.includes(classId)) {
      newSelected = currentSelected.filter(id => id !== classId);
    } else {
      newSelected = [...currentSelected, classId];
    }
    
    setAssignForm({ 
      ...assignForm, 
      selected_classes: newSelected
    });
  };

  // Update fee when payment plan changes
  const handlePaymentPlanChange = (paymentFrequency) => {
    const fees = calculateFee(assignForm.program_id, paymentFrequency);
    setAssignForm({
      ...assignForm,
      payment_frequency: paymentFrequency,
      monthly_fee: fees.monthly,
      total_fee: fees.total
    });
  };

  const handleSavePlayer = async () => {
    try {
      if (isEditing && selectedPlayer) {
        await api.put(`/players/${selectedPlayer.id}`, playerForm);
        toast.success('Player updated successfully');
      } else {
        // Admin creating player - need to use a different endpoint or include parent_id
        await api.post('/admin/players', playerForm);
        toast.success('Player created successfully');
      }
      setAddEditDialog(false);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Failed to save player');
      }
    }
  };

  const handleAssignPlayer = async () => {
    if (!selectedPlayer || assignForm.selected_classes.length === 0) {
      toast.error('Please select at least one class');
      return;
    }
    
    try {
      // Create ONE enrollment with multiple class_ids
      const response = await api.post('/admin/enrollments', {
        player_id: selectedPlayer.id,
        class_ids: assignForm.selected_classes,  // Multiple classes
        program_id: assignForm.program_id,
        payment_frequency: assignForm.payment_frequency,
        monthly_fee: assignForm.monthly_fee ? parseFloat(assignForm.monthly_fee) : null,
        status: 'current'
      });
      
      const classCount = assignForm.selected_classes.length;
      
      // Show detailed success message with subscription info
      if (response.data.is_prorated) {
        toast.success(
          `Player enrolled! Prorated amount: ${cs()}${response.data.prorated_amount} (${response.data.proration_sessions}/${response.data.total_sessions} sessions). Next billing: ${response.data.next_billing_date}`,
          { duration: 6000 }
        );
      } else {
        toast.success(
          `Player enrolled in program with ${classCount} class${classCount > 1 ? 'es' : ''}. Monthly fee: ${cs()}${response.data.monthly_fee}`,
          { duration: 4000 }
        );
      }
      
      setAssignDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign player');
    }
  };

  // Filter classes by selected program
  const filteredClasses = assignForm.program_id 
    ? classes.filter(c => c.program_id === assignForm.program_id)
    : classes;

  const columns = [
    {
      key: 'numeric_id',
      label: 'Player ID',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => <span className="font-mono text-xs text-slate-500">{row.numeric_id || '-'}</span>
    },
    {
      key: 'full_name',
      label: 'Player',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 font-semibold">
              {(row.full_name || row.name)?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/admin/players/${row.id}`)}
                className="font-medium text-blue-600 hover:text-blue-700 hover:underline text-left"
                data-testid={`player-name-link-${row.id}`}
              >
                {row.full_name || row.name}
              </button>
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'date_of_birth',
      label: 'Date of Birth',
      sortable: true,
      render: (row) => row.date_of_birth ? new Date(row.date_of_birth).toLocaleDateString() : '-'
    },
    {
      key: 'age',
      label: 'Age',
      sortable: true,
      render: (row) => row.date_of_birth ? `${calculateAge(row.date_of_birth)} years` : '-'
    },
    {
      key: 'gender',
      label: 'Gender',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' }
      ],
      render: (row) => <span className="capitalize">{row.gender || '-'}</span>
    },
    {
      key: 'parent_name',
      label: 'Parent',
      sortable: true,
      filterable: true,
      filterType: 'text'
    },
    {
      key: 'parent_email',
      label: 'Parent Email',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => row.parent_email || '-'
    },
    {
      key: 'parent_phone',
      label: 'Parent Phone',
      sortable: true,
      render: (row) => row.parent_phone || '-'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'active', label: 'Active' },
        { value: 'paused', label: 'Paused' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'not_enrolled', label: 'Not Enrolled' }
      ],
      render: (row) => {
        const status = getPlayerStatus(row);
        const statusConfig = {
          active: { label: 'Active', className: 'bg-green-100 text-green-700', icon: CheckCircle },
          paused: { label: 'Paused', className: 'bg-amber-100 text-amber-700', icon: PauseCircle },
          cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700', icon: XCircle },
          not_enrolled: { label: 'Not Enrolled', className: 'bg-slate-100 text-slate-600', icon: UserX }
        };
        const config = statusConfig[status];
        const Icon = config.icon;
        return (
          <Badge className={`${config.className} flex items-center gap-1 w-fit`}>
            <Icon className="h-3 w-3" />
            {config.label}
          </Badge>
        );
      }
    },
    {
      key: 'programs_display',
      label: 'Programs',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <span className="text-sm">
          {row.programs_display || <span className="text-slate-400">Not enrolled</span>}
        </span>
      )
    },
    {
      key: 'skill_level',
      label: 'Skill Level',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'beginner', label: 'Beginner' },
        { value: 'intermediate', label: 'Intermediate' },
        { value: 'advanced', label: 'Advanced' },
        { value: 'competitive', label: 'Competitive' }
      ],
      render: (row) => (
        <Badge className={`capitalize ${
          row.skill_level === 'competitive' ? 'bg-red-100 text-red-700' :
          row.skill_level === 'advanced' ? 'bg-purple-100 text-purple-700' :
          row.skill_level === 'intermediate' ? 'bg-blue-100 text-blue-700' :
          'bg-slate-100 text-slate-700'
        }`}>
          {row.skill_level || 'beginner'}
        </Badge>
      )
    },
    {
      key: 'enrolled_classes',
      label: 'Classes',
      sortable: true,
      render: (row) => (
        <span className="font-medium text-slate-900">
          {row.enrolled_classes?.length || 0} classes
        </span>
      )
    },
    // Uniform fields
    {
      key: 'shirt_name',
      label: 'Shirt Name',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => row.shirt_name || '-'
    },
    {
      key: 'shirt_number',
      label: 'Shirt Number',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => row.shirt_number || '-'
    },
    // Measurements
    {
      key: 'chest_size',
      label: 'Chest Size',
      sortable: true,
      render: (row) => row.chest_size || '-'
    },
    {
      key: 'shirt_length',
      label: 'Shirt Length',
      sortable: true,
      render: (row) => row.shirt_length || '-'
    },
    {
      key: 'sleeves',
      label: 'Sleeves',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'short', label: 'Short' },
        { value: 'full', label: 'Full' }
      ],
      render: (row) => row.sleeves ? <span className="capitalize">{row.sleeves}</span> : '-'
    },
    {
      key: 'waist_size',
      label: 'Waist Size',
      sortable: true,
      render: (row) => row.waist_size || '-'
    },
    {
      key: 'trouser_length',
      label: 'Trouser Length',
      sortable: true,
      render: (row) => row.trouser_length || '-'
    },
    // Medical & Notes
    {
      key: 'medical_notes',
      label: 'Medical Notes',
      sortable: false,
      render: (row) => row.medical_notes ? (
        <span className="text-xs text-slate-600 max-w-[150px] truncate block" title={row.medical_notes}>
          {row.medical_notes}
        </span>
      ) : '-'
    },
    {
      key: 'notes',
      label: 'Notes',
      sortable: false,
      render: (row) => row.notes ? (
        <span className="text-xs text-slate-600 max-w-[150px] truncate block" title={row.notes}>
          {row.notes}
        </span>
      ) : '-'
    },
    {
      key: 'cricclubs_id',
      label: 'CricClubs ID',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => row.cricclubs_id || '-'
    },
    {
      key: 'cricclubs_email',
      label: 'CricClubs Email',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => row.cricclubs_email || '-'
    },
    {
      key: 'player_mobile',
      label: 'Player Mobile',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => row.player_mobile || '-'
    },
    {
      key: 'player_email',
      label: 'Player Email',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => row.player_email || '-'
    },
    {
      key: 'created_at',
      label: 'Joined',
      sortable: true,
      render: (row) => new Date(row.created_at).toLocaleDateString()
    }
  ];

  const actions = (row) => (
    <div className="flex items-center gap-1">
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate(`/admin/players/${row.id}`)}
        data-testid={`view-player-${row.id}`}
      >
        <Eye className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => openEditDialog(row)}
        data-testid={`edit-player-${row.id}`}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => openAssignDialog(row)}
        className="text-green-600 hover:text-green-700"
        data-testid={`assign-player-${row.id}`}
      >
        <GraduationCap className="h-4 w-4" />
      </Button>
    </div>
  );

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <DashboardLayout title="Players">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Players">
      <div className="space-y-6" data-testid="players-page">
        <div className="space-y-3">
          <p className="text-slate-600">Manage all enrolled players</p>
          <div className="flex flex-wrap items-center gap-3">
            <ColumnConfig
              columns={columns}
              visibleColumns={visibleColumns}
              onColumnChange={setVisibleColumns}
              storageKey="admin-players-columns"
              requiredColumns={['name']}
            />
            <BulkActions
              entityName="Players"
              exportEndpoint="/admin/players/export"
              uploadEndpoint="/admin/players/bulk-upload"
              onUploadComplete={fetchData}
              sampleFields={['full_name', 'date_of_birth', 'parent_email']}
            />
            <BulkActions
              entityName="Enrollments"
              uploadEndpoint="/admin/enrollments/bulk-assign"
              onUploadComplete={fetchData}
              sampleFields={['player_name', 'parent_email', 'program_name', 'class_name']}
              uploadButtonLabel="Bulk Assign"
              showExport={false}
              dialogTitle="Bulk Assign Players to Programs"
              dialogDescription="Upload a CSV to assign multiple players to programs. Players already enrolled will be skipped. Subscriptions will be created with prorated fees."
            />
            <Button onClick={openAddDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="add-player-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Player
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full overflow-x-auto lg:w-auto lg:inline-flex">
            <TabsTrigger value="all" className="flex items-center gap-2" data-testid="tab-all">
              <Users className="h-4 w-4" />
              All ({statusCounts.all})
            </TabsTrigger>
            <TabsTrigger value="active" className="flex items-center gap-2" data-testid="tab-active">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Active ({statusCounts.active})
            </TabsTrigger>
            <TabsTrigger value="paused" className="flex items-center gap-2" data-testid="tab-paused">
              <PauseCircle className="h-4 w-4 text-amber-600" />
              Paused ({statusCounts.paused})
            </TabsTrigger>
            <TabsTrigger value="cancelled" className="flex items-center gap-2" data-testid="tab-cancelled">
              <XCircle className="h-4 w-4 text-red-600" />
              Cancelled ({statusCounts.cancelled})
            </TabsTrigger>
            <TabsTrigger value="not_enrolled" className="flex items-center gap-2" data-testid="tab-not-enrolled">
              <UserX className="h-4 w-4 text-slate-500" />
              Not Enrolled ({statusCounts.not_enrolled})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {/* Bulk Selection Toolbar */}
            {selectedPlayerIds.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-blue-700">
                    {selectedPlayerIds.length} player(s) selected
                  </span>
                  <Button variant="ghost" size="sm" onClick={clearSelection} className="text-blue-600">
                    <XCircle className="h-4 w-4 mr-1" />
                    Clear Selection
                  </Button>
                </div>
                <Button onClick={openBulkUpdateDialog} data-testid="bulk-update-btn">
                  Bulk Update Fields
                </Button>
              </div>
            )}
            
            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <DataTable
                  data={getFilteredPlayers()}
                  columns={columns}
                  visibleColumns={visibleColumns}
                  actions={actions}
                  emptyIcon={Users}
                  emptyMessage={activeTab === 'all' ? "No players enrolled yet." : `No ${activeTab.replace('_', ' ')} players.`}
                  searchable={true}
                  searchFields={['name', 'full_name', 'parent_name', 'skill_level']}
                  testIdPrefix="players"
                  selectable={true}
                  selectedRows={selectedPlayerIds}
                  onSelectionChange={setSelectedPlayerIds}
                  rowIdKey="id"
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add/Edit Player Dialog */}
        <Dialog open={addEditDialog} onOpenChange={setAddEditDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-blue-500" />
                {isEditing ? 'Edit Player' : 'Add New Player'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={playerForm.full_name}
                  onChange={(e) => setPlayerForm({ ...playerForm, full_name: e.target.value })}
                  placeholder="Player's full name"
                  data-testid="player-name-input"
                />
              </div>

              {!isEditing && (
                <div className="space-y-2">
                  <Label>Parent *</Label>
                  <Select
                    value={playerForm.parent_id}
                    onValueChange={(value) => setPlayerForm({ ...playerForm, parent_id: value })}
                  >
                    <SelectTrigger data-testid="player-parent-select">
                      <SelectValue placeholder="Select parent" />
                    </SelectTrigger>
                    <SelectContent>
                      {parents.map(parent => (
                        <SelectItem key={parent.id} value={parent.id}>
                          {parent.name} ({parent.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={playerForm.date_of_birth}
                  onChange={(e) => setPlayerForm({ ...playerForm, date_of_birth: e.target.value })}
                  data-testid="player-dob-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select
                    value={playerForm.gender}
                    onValueChange={(value) => setPlayerForm({ ...playerForm, gender: value })}
                  >
                    <SelectTrigger data-testid="player-gender-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Skill Level</Label>
                  <Select
                    value={playerForm.skill_level}
                    onValueChange={(value) => setPlayerForm({ ...playerForm, skill_level: value })}
                  >
                    <SelectTrigger data-testid="player-skill-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Medical Notes</Label>
                <Textarea
                  value={playerForm.medical_notes}
                  onChange={(e) => setPlayerForm({ ...playerForm, medical_notes: e.target.value })}
                  placeholder="Any medical conditions, allergies, or special needs..."
                  rows={3}
                  data-testid="player-medical-input"
                />
              </div>

              {/* Measurements Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-slate-700 mb-3">👕 Uniform Information</h4>
                
                {/* Shirt Personalization */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 mb-4">
                  <p className="text-sm font-medium text-blue-700 mb-2">Shirt Personalization</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name on Shirt</Label>
                      <Input
                        type="text"
                        maxLength="20"
                        value={playerForm.shirt_name}
                        onChange={(e) => setPlayerForm({ ...playerForm, shirt_name: e.target.value.toUpperCase() })}
                        placeholder="e.g., SMITH"
                        className="uppercase"
                        data-testid="player-shirt-name-input"
                      />
                      <p className="text-xs text-slate-500">Max 20 characters</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Jersey Number</Label>
                      <Input
                        type="number"
                        min="0"
                        max="99"
                        value={playerForm.shirt_number}
                        onChange={(e) => setPlayerForm({ ...playerForm, shirt_number: e.target.value })}
                        placeholder="0-99"
                        data-testid="player-shirt-number-input"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Measurements */}
                <p className="text-sm text-slate-500 mb-2">Measurements (in inches)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Chest Size (inches)</Label>
                    <Input
                      type="number"
                      min="20"
                      max="46"
                      value={playerForm.chest_size}
                      onChange={(e) => setPlayerForm({ ...playerForm, chest_size: e.target.value })}
                      placeholder="20-46"
                      data-testid="player-chest-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Shirt Length (inches)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={playerForm.shirt_length}
                      onChange={(e) => setPlayerForm({ ...playerForm, shirt_length: e.target.value })}
                      placeholder="e.g., 28"
                      data-testid="player-shirt-length-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sleeves</Label>
                    <Select
                      value={playerForm.sleeves}
                      onValueChange={(value) => setPlayerForm({ ...playerForm, sleeves: value })}
                    >
                      <SelectTrigger data-testid="player-sleeves-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">Short</SelectItem>
                        <SelectItem value="full">Full</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Waist Size (inches)</Label>
                    <Input
                      type="number"
                      min="20"
                      max="46"
                      value={playerForm.waist_size}
                      onChange={(e) => setPlayerForm({ ...playerForm, waist_size: e.target.value })}
                      placeholder="20-46"
                      data-testid="player-waist-input"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Trouser Length (inches)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={playerForm.trouser_length}
                      onChange={(e) => setPlayerForm({ ...playerForm, trouser_length: e.target.value })}
                      placeholder="e.g., 32"
                      data-testid="player-trouser-input"
                    />
                  </div>
                </div>
              </div>

              {/* CricClubs Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-slate-700 mb-3">CricClubs Info</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>CricClubs ID</Label>
                    <Input
                      value={playerForm.cricclubs_id}
                      onChange={(e) => setPlayerForm({ ...playerForm, cricclubs_id: e.target.value })}
                      placeholder="e.g., CC12345"
                      data-testid="player-cricclubs-id-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CricClubs Email</Label>
                    <Input
                      type="email"
                      value={playerForm.cricclubs_email}
                      onChange={(e) => setPlayerForm({ ...playerForm, cricclubs_email: e.target.value })}
                      placeholder="email@cricclubs.com"
                      data-testid="player-cricclubs-email-input"
                    />
                  </div>
                </div>
              </div>

              {/* Player Contact Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-slate-700 mb-3">Player Contact Info</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Player Mobile</Label>
                    <Input
                      type="tel"
                      value={playerForm.player_mobile}
                      onChange={(e) => setPlayerForm({ ...playerForm, player_mobile: e.target.value })}
                      placeholder="e.g., 555-123-4567"
                      data-testid="player-mobile-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Player Email</Label>
                    <Input
                      type="email"
                      value={playerForm.player_email}
                      onChange={(e) => setPlayerForm({ ...playerForm, player_email: e.target.value })}
                      placeholder="player@email.com"
                      data-testid="player-email-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setAddEditDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-blue-500 hover:bg-blue-600"
                onClick={handleSavePlayer}
                data-testid="save-player-btn"
              >
                {isEditing ? 'Update Player' : 'Add Player'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign to Class Dialog */}
        <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-green-500" />
                Assign to Program/Class
              </DialogTitle>
            </DialogHeader>
            
            {selectedPlayer && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedPlayer.full_name || selectedPlayer.name}</p>
                  <p className="text-sm text-slate-500">
                    {selectedPlayer.skill_level && `Skill: ${selectedPlayer.skill_level}`}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Program *</Label>
                  <Select
                    value={assignForm.program_id}
                    onValueChange={handleProgramChange}
                  >
                    <SelectTrigger data-testid="assign-program-select">
                      <SelectValue placeholder="Select program" />
                    </SelectTrigger>
                    <SelectContent>
                      {programs.filter(p => p.is_active).map(program => (
                        <SelectItem key={program.id} value={program.id}>
                          {program.name} - {cs()}{program.price}/mo
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Multi-select Classes with Checkboxes */}
                {assignForm.program_id && (
                  <div className="space-y-2">
                    <Label>Classes * <span className="text-slate-500 font-normal">(Select one or more)</span></Label>
                    <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2 bg-slate-50">
                      {filteredClasses.filter(c => c.is_active).length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-2">No classes available for this program</p>
                      ) : (
                        filteredClasses.filter(c => c.is_active).map(cls => (
                          <label 
                            key={cls.id} 
                            className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-white transition-colors ${
                              assignForm.selected_classes.includes(cls.id) ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-transparent'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={assignForm.selected_classes.includes(cls.id)}
                              onChange={() => handleClassToggle(cls.id)}
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              data-testid={`class-checkbox-${cls.id}`}
                            />
                            <div className="flex-1">
                              <p className="font-medium text-sm">{cls.name}</p>
                              <p className="text-xs text-slate-500">
                                {cls.schedule_day} {cls.start_time}-{cls.end_time}
                                {cls.coach_name && ` • ${cls.coach_name}`}
                              </p>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                    {assignForm.selected_classes.length > 0 && (
                      <p className="text-xs text-blue-600">{assignForm.selected_classes.length} class(es) selected</p>
                    )}
                  </div>
                )}

                {/* Payment Plan */}
                <div className="space-y-2">
                  <Label>Payment Plan</Label>
                  <Select
                    value={assignForm.payment_frequency}
                    onValueChange={handlePaymentPlanChange}
                  >
                    <SelectTrigger data-testid="assign-payment-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const program = programs.find(p => p.id === assignForm.program_id);
                        return (
                          <>
                            <SelectItem value="monthly">
                              Monthly - {cs()}{program?.price || 0}/mo
                            </SelectItem>
                            <SelectItem value="quarterly">
                              Quarterly - {cs()}{program?.quarterly_price || program?.price || 0}/mo
                            </SelectItem>
                            <SelectItem value="biannual">
                              Bi-Annual - {cs()}{program?.biannual_price || program?.price || 0}/mo
                            </SelectItem>
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                </div>

                {/* Fee Summary */}
                {assignForm.program_id && (
                  <div className="p-4 bg-slate-100 rounded-lg space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">
                        {assignForm.payment_frequency === 'monthly' ? 'Monthly' : 
                         assignForm.payment_frequency === 'quarterly' ? 'Quarterly' : 'Bi-Annual'} Rate:
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">$</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={assignForm.monthly_fee}
                          onChange={(e) => {
                            const newRate = e.target.value;
                            const multiplier = assignForm.payment_frequency === 'quarterly' ? 3 : 
                                              assignForm.payment_frequency === 'biannual' ? 6 : 1;
                            const total = (parseFloat(newRate) || 0) * multiplier;
                            setAssignForm({ 
                              ...assignForm, 
                              monthly_fee: newRate,
                              total_fee: total.toFixed(2)
                            });
                          }}
                          className="w-24 h-8 text-right"
                          data-testid="assign-fee-input"
                        />
                        <span className="text-sm text-slate-500">/mo</span>
                      </div>
                    </div>
                    {assignForm.payment_frequency !== 'monthly' && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-500">
                          {assignForm.payment_frequency === 'quarterly' ? '3 months' : '6 months'}:
                        </span>
                        <span className="text-slate-600">
                          {cs()}{assignForm.monthly_fee} × {assignForm.payment_frequency === 'quarterly' ? 3 : 6}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                      <span className="font-semibold">Total Due:</span>
                      <span className="font-bold text-lg text-blue-600">{cs()}{assignForm.total_fee || assignForm.monthly_fee || '0.00'}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setAssignDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleAssignPlayer}
                disabled={assignForm.selected_classes.length === 0}
                data-testid="assign-player-btn"
              >
                Assign to {assignForm.selected_classes.length || ''} Class{assignForm.selected_classes.length !== 1 ? 'es' : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Update Dialog */}
        <Dialog open={bulkUpdateDialog} onOpenChange={setBulkUpdateDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                Bulk Update {selectedPlayerIds.length} Player(s)
              </DialogTitle>
              <DialogDescription>
                Only filled fields will be updated. Leave fields empty to keep existing values.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Skill Level</Label>
                  <Select
                    value={bulkUpdateForm.skill_level || "__none__"}
                    onValueChange={(value) => setBulkUpdateForm({ ...bulkUpdateForm, skill_level: value === "__none__" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="No change" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No change</SelectItem>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                      <SelectItem value="competitive">Competitive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select
                    value={bulkUpdateForm.gender || "__none__"}
                    onValueChange={(value) => setBulkUpdateForm({ ...bulkUpdateForm, gender: value === "__none__" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="No change" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No change</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Uniform Details</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Shirt Name</Label>
                    <Input
                      value={bulkUpdateForm.shirt_name}
                      onChange={(e) => setBulkUpdateForm({ ...bulkUpdateForm, shirt_name: e.target.value })}
                      placeholder="No change"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Shirt Number</Label>
                    <Input
                      value={bulkUpdateForm.shirt_number}
                      onChange={(e) => setBulkUpdateForm({ ...bulkUpdateForm, shirt_number: e.target.value })}
                      placeholder="No change"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Measurements</Label>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Chest Size</Label>
                    <Input
                      value={bulkUpdateForm.chest_size}
                      onChange={(e) => setBulkUpdateForm({ ...bulkUpdateForm, chest_size: e.target.value })}
                      placeholder="No change"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Shirt Length</Label>
                    <Input
                      value={bulkUpdateForm.shirt_length}
                      onChange={(e) => setBulkUpdateForm({ ...bulkUpdateForm, shirt_length: e.target.value })}
                      placeholder="No change"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Sleeves</Label>
                    <Select
                      value={bulkUpdateForm.sleeves || "__none__"}
                      onValueChange={(value) => setBulkUpdateForm({ ...bulkUpdateForm, sleeves: value === "__none__" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="No change" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No change</SelectItem>
                        <SelectItem value="short">Short</SelectItem>
                        <SelectItem value="full">Full</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Waist Size</Label>
                    <Input
                      value={bulkUpdateForm.waist_size}
                      onChange={(e) => setBulkUpdateForm({ ...bulkUpdateForm, waist_size: e.target.value })}
                      placeholder="No change"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Trouser Length</Label>
                    <Input
                      value={bulkUpdateForm.trouser_length}
                      onChange={(e) => setBulkUpdateForm({ ...bulkUpdateForm, trouser_length: e.target.value })}
                      placeholder="No change"
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setBulkUpdateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={executeBulkUpdate}
                disabled={bulkUpdateLoading}
                data-testid="execute-bulk-update-btn"
              >
                {bulkUpdateLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>Update {selectedPlayerIds.length} Player(s)</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Players;
